import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { Button } from "@/components/ui/button";
import { HKBadge } from "@/components/site/HKBadge";
import { CTASection } from "@/components/site/CTASection";
import { fetchPost } from "@/lib/api";
import { Seo } from "@/components/site/Seo";

const formatDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); } catch { return iso; } };

export default function InsightDetailPage() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchPost(slug).then((d) => { if (alive) setPost(d); }).catch(() => { if (alive) setNotFound(true); });
    return () => { alive = false; };
  }, [slug]);

  if (notFound) {
    return (
      <div data-testid="insight-not-found" className="mx-auto w-full max-w-3xl px-4 py-24 text-center">
        <Seo title="Blog post not found" path={`/blog/${slug}`} noindex />
        <h1 className="text-4xl font-extrabold text-slate-900">{t("pages.insightNotFound")}</h1>
        <p className="mt-3 text-slate-600">{t("pages.insightNotFoundDesc")}</p>
        <Link to="/blog" className="mt-6 inline-block"><Button variant="outline" className="h-10"><ArrowLeft className="mr-1.5 h-4 w-4" /> {t("pages.allInsights")}</Button></Link>
      </div>
    );
  }

  return (
    <div data-testid="insight-detail-page">
      {post && (
        <Seo
          title={post.meta_title || post.title}
          description={post.meta_description || post.excerpt}
          keywords={(post.keywords || []).join(", ") || undefined}
          path={`/blog/${post.slug}`}
          type="article"
          image={post.cover_image || undefined}
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": post.title,
            "description": post.meta_description || post.excerpt,
            "keywords": (post.keywords || []).join(", ") || undefined,
            "image": post.cover_image || undefined,
            "articleSection": post.category || "Insight",
            "inLanguage": post.locale || "en",
            "datePublished": post.created_at,
            "dateModified": post.updated_at || post.created_at,
            "author": { "@type": "Organization", "name": post.author || "Danny Clans Media" },
            "publisher": { "@type": "Organization", "name": "Danny Clans Media", "logo": { "@type": "ImageObject", "url": "https://dannyclans.solutions/logo.png" } },
            "mainEntityOfPage": { "@type": "WebPage", "@id": `https://dannyclans.solutions/blog/${post.slug}` },
          }}
        />
      )}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dc-dot-grid opacity-50" aria-hidden />
        <div className="relative mx-auto w-full max-w-3xl px-4 pb-6 pt-14 sm:px-6 sm:pt-20 lg:px-8">
          <Link to="/blog" className="inline-flex items-center text-sm font-semibold text-blue-700"><ArrowLeft className="mr-1 h-4 w-4" /> {t("pages.allInsights")}</Link>
          {post ? (
            <>
              <div className="mt-5 flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-blue-700">
                <span>{post.category || t("pages.insightCategory")}</span>
                <span className="text-slate-300">•</span>
                <span className="flex items-center gap-1 text-slate-500"><Calendar className="h-3.5 w-3.5" /> {formatDate(post.created_at)}</span>
                <span className="text-slate-300">•</span>
                <span className="flex items-center gap-1 text-slate-500"><User className="h-3.5 w-3.5" /> {post.author}</span>
              </div>
              <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl lg:leading-[1.08]">{post.title}</h1>
              <p className="mt-4 text-base text-slate-600 sm:text-lg">{post.excerpt}</p>
              <HKBadge className="mt-5" />
            </>
          ) : (
            <div className="mt-5 space-y-3">
              <div className="h-4 w-32 animate-pulse rounded bg-slate-200" />
              <div className="h-12 w-3/4 animate-pulse rounded bg-slate-200" />
              <div className="h-4 w-full animate-pulse rounded bg-slate-200" />
            </div>
          )}
        </div>
      </section>

      {post && post.cover_image && (
        <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="aspect-[16/9] w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
            <img src={post.cover_image} alt={`${post.title} — article cover`} decoding="async" fetchpriority="high" className="h-full w-full object-cover" />
          </div>
        </div>
      )}

      <article className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6 sm:py-16 lg:px-8">
        {post && (
          <div
            data-testid="insight-content"
            className="prose prose-slate max-w-none text-base leading-relaxed text-slate-800
              prose-headings:font-extrabold prose-headings:tracking-tight prose-headings:text-slate-900
              prose-h1:text-3xl prose-h1:mt-8 prose-h1:mb-4 sm:prose-h1:text-4xl
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-3 sm:prose-h2:text-3xl
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-2
              prose-p:my-4 prose-p:text-slate-700
              prose-a:font-semibold prose-a:text-blue-700 prose-a:no-underline hover:prose-a:underline
              prose-strong:text-slate-900
              prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6
              prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-6
              prose-li:my-1
              prose-hr:my-8 prose-hr:border-slate-200"
          >
            <ReactMarkdown
              components={{
                a: ({ href, children, ...props }) => (
                  <a href={href || "#"} {...props} rel="noopener">{children}</a>
                ),
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>
        )}
      </article>
      <CTASection />
    </div>
  );
}
